import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MfaCodeInput } from '../../components/auth/MfaCodeInput';
import { MfaMethodPicker } from '../../components/auth/MfaMethodPicker';
import { useAuth } from '../../contexts/AuthContext';
import { mfaApi } from '../../lib/api';
import { BRAND } from '../../lib/theme';
import type { MfaMethod } from '../../types';

interface MfaChallengePageProps {
  dark?: boolean;
  onSuccess: () => void;
  onUseBackup: () => void;
  onCancel: () => void;
}

const RESEND_COOLDOWN_SECS = 300; // 5 minutes, matches server TTL

/**
 * Login step 2: MFA challenge verification.
 *
 * - If only one method: skips method picker, shows code input directly.
 * - For TOTP: no "send" step, just code input.
 * - For email/sms: sends challenge on mount (and on resend click).
 * - Auto-submits on 6th digit.
 * - Backup-code link navigates to MfaBackupCodePage.
 */
export function MfaChallengePage({
  dark = false,
  onSuccess,
  onUseBackup,
  onCancel,
}: MfaChallengePageProps) {
  const { t } = useTranslation();
  const { mfaState, completeMfa, cancelMfa, setChallengeId } = useAuth();

  const available = mfaState?.availableMethods ?? [];
  const defaultMethod: MfaMethod = mfaState?.primaryMethod ?? available[0] ?? 'email';

  const [selectedMethod, setSelectedMethod] = useState<MfaMethod>(defaultMethod);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback(() => {
    setCooldown(RESEND_COOLDOWN_SECS);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Send challenge for email/sms on mount (or when method changes away from TOTP)
  const sendChallenge = useCallback(async (method: MfaMethod) => {
    if (method === 'totp') return; // TOTP needs no server send
    if (!mfaState?.userIdHint) return;

    setLoading(true);
    setError('');
    try {
      const result = await mfaApi.sendChallenge(mfaState.userIdHint, method);
      setChallengeId(result.challenge_id);
      setCodeSent(true);
      startCooldown();
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { errors?: { code?: string }[] } } })?.response?.data?.errors?.[0];
      if (errData?.code === 'E_RATE_LIMIT') {
        setError(t('mfa.setup.step_confirm.locked'));
        setCooldown(900); // 15 min lockout
      } else {
        setError(t('mfa.challenge.send_failed'));
      }
    } finally {
      setLoading(false);
    }
  }, [mfaState?.userIdHint, setChallengeId, startCooldown, t]);

  useEffect(() => {
    sendChallenge(selectedMethod);
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  const handleMethodChange = (method: MfaMethod) => {
    setSelectedMethod(method);
    setError('');
    setCodeSent(false);
    setResetKey(k => k + 1);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setCooldown(0);
    sendChallenge(method);
  };

  const handleCodeComplete = async (code: string) => {
    if (!mfaState?.userIdHint) return;
    setLoading(true);
    setError('');
    try {
      const result = await mfaApi.verifyChallenge({
        challenge_id: selectedMethod !== 'totp' ? (mfaState.challengeId ?? null) : null,
        code,
        user_id_hint: mfaState.userIdHint,
      });
      completeMfa(result);
      onSuccess();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 429) {
        setError(t('mfa.setup.step_confirm.locked'));
      } else {
        setError(t('mfa.setup.step_confirm.invalid_code'));
      }
      setResetKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    cancelMfa();
    onCancel();
  };

  const handleResend = () => {
    if (cooldown > 0) return;
    setResetKey(k => k + 1);
    sendChallenge(selectedMethod);
  };

  const formatCooldown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const codeLabel = t(`mfa.challenge.code_input_label.${selectedMethod}`);

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      alignItems: 'center',
      justifyContent: 'center',
      background: dark ? '#0b101a' : '#f9fafb',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 420,
        background: dark ? '#141b27' : '#fff',
        border: `1px solid ${dark ? '#1e2735' : '#e4e7ec'}`,
        borderRadius: 16,
        padding: 32,
        boxShadow: dark ? 'none' : '0 4px 24px rgba(16,24,40,0.08)',
      }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#f2f4f7' : '#101828', marginBottom: 8 }}>
          {t('mfa.challenge.title')}
        </h2>

        {/* Method picker — only shown when multiple methods available */}
        {available.length > 1 && (
          <div style={{ marginBottom: 20 }}>
            <MfaMethodPicker
              available={available}
              selected={selectedMethod}
              onSelect={handleMethodChange}
            />
          </div>
        )}

        {/* Code input section */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 13, color: dark ? '#8392a7' : '#667085', marginBottom: 16 }}>
            {codeLabel}
          </p>
          <MfaCodeInput
            onComplete={handleCodeComplete}
            disabled={loading}
            error={!!error}
            resetKey={resetKey}
          />
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            background: '#fef3f2',
            border: '1px solid #fecdca',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 12,
            color: '#b42318',
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {/* Resend button — hidden for TOTP */}
        {selectedMethod !== 'totp' && (
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <span style={{ fontSize: 12, color: dark ? '#8392a7' : '#667085' }}>
              {t('mfa.setup.step_confirm.resend_label')}{' '}
            </span>
            <button
              type="button"
              onClick={handleResend}
              disabled={cooldown > 0 || loading || !codeSent}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 12,
                color: cooldown > 0 ? '#98a2b3' : BRAND,
                cursor: cooldown > 0 ? 'default' : 'pointer',
                fontWeight: 500,
                padding: 0,
              }}
            >
              {cooldown > 0
                ? t('mfa.setup.step_confirm.resend_cooldown', { time: formatCooldown(cooldown) })
                : t('mfa.setup.step_confirm.resend_button')}
            </button>
          </div>
        )}

        {/* Backup code link */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <button
            type="button"
            onClick={onUseBackup}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 13,
              color: BRAND,
              cursor: 'pointer',
              fontWeight: 500,
              textDecoration: 'underline',
            }}
          >
            {t('mfa.challenge.use_backup')}
          </button>
        </div>

        {/* Cancel */}
        <button
          type="button"
          onClick={handleCancel}
          style={{
            width: '100%',
            background: 'transparent',
            border: `1px solid ${dark ? '#1e2735' : '#e4e7ec'}`,
            borderRadius: 9,
            padding: '10px',
            fontSize: 13,
            fontWeight: 500,
            color: dark ? '#cfd7e3' : '#344054',
            cursor: 'pointer',
          }}
        >
          {t('common.action.cancel')}
        </button>
      </div>
    </div>
  );
}
